import { useEffect, useEffectEvent, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useFeatureFlagEnabled } from '@/lib/posthog/hooks/useFeatureFlagEnabled';
import { useTrackEvent } from '@/lib/posthog/hooks/useTrackEvent';
import { detectInAppBrowser } from '@/lib/posthog/inAppBrowser';
import { AuthError } from '@/modules/auth/components/AuthError';
import { GoogleLogin } from '@/modules/auth/components/GoogleLogin';
import { LinkedInLogin } from '@/modules/auth/components/LinkedInLogin';
import { SlackLogin } from '@/modules/auth/components/SlackLogin';
import {
  type AuthMethod,
  businessEmailRequiredMessage,
  getAuthFailureReason,
  isBusinessEmailRequiredError,
  isInvalidTrialOfferError,
  trialOfferFailureMessage,
  trialOfferParamKey,
} from '@/modules/auth/constants';
import { detectMobileOs } from '@/modules/auth/inAppHandoff';
import { getSignUpParams } from '@/modules/auth/signUpParams';
import { isSlackSsoConfigured } from '@/modules/auth/slackSso';
import { TermsNotice } from '@/modules/legal/components/terms-notice/TermsNotice';
import { useLegalDocumentsQuery } from '@/modules/legal/queries/useLegalDocumentsQuery';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';

import { ContinueOnComputer } from './continueOnComputer/ContinueOnComputer';
import { InAppGoogleSignUp } from './inAppGoogle/InAppGoogleSignUp';
import { SignUpLayout } from './signUpLayout/SignUpLayout';

const SIGN_UP_FAILED_MESSAGE = 'We couldn’t create the account. Try again.';

export const SignUp = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isLinkedInSsoEnabled = useFeatureFlagEnabled('linkedinSso');
  const terms = useLegalDocumentsQuery().data?.find(
    (document) => document.type === 'TermsAndConditions',
  );
  const acceptTerms = Boolean(terms?.url);

  const trialOfferCode = searchParams.get(trialOfferParamKey) ?? undefined;
  const { oauthReturnTo, appQuery } = getSignUpParams(searchParams);

  const trackEvent = useTrackEvent();
  const inAppBrowser = detectInAppBrowser(navigator.userAgent);

  const [isSignedUp, setIsSignedUp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentUserQuery = useCurrentUserQuery({
    enabled: isSignedUp,
  });

  const onSignedUp = useEffectEvent(() => {
    const isHiringManager =
      currentUserQuery.data?.roles?.includes('HiringManager');
    const basePath = isHiringManager ? '/hm' : '/';

    if (appQuery) {
      navigate(`${basePath}?${appQuery}`);
    } else {
      navigate(basePath);
    }
  });

  useEffect(() => {
    if (currentUserQuery.isSuccess && currentUserQuery.data) {
      onSignedUp();
    }
  }, [currentUserQuery.isSuccess, currentUserQuery.data]);

  const handleSuccess = (authMethod: AuthMethod) => {
    trackEvent('userSignedUp', {
      authMethod,
    });

    setError(null);
    setIsSignedUp(true);
  };

  const handleError = (authMethod: AuthMethod, error?: Error) => {
    trackEvent('signUpFailed', {
      authMethod,
      reason: getAuthFailureReason(error, authMethod),
    });
    setIsSignedUp(false);
    if (isBusinessEmailRequiredError(error)) {
      setError(businessEmailRequiredMessage);
      return;
    }

    setError(
      trialOfferCode && isInvalidTrialOfferError(error)
        ? trialOfferFailureMessage
        : SIGN_UP_FAILED_MESSAGE,
    );
  };

  const offerNotice = trialOfferCode
    ? 'One month of Finlay Source, free. No card required – continue on Free afterward.'
    : 'No credit card required.';

  const linkedIn = isLinkedInSsoEnabled
    ? {
        returnTo: oauthReturnTo,
        acceptTerms,
        intent: 'signUp' as const,
        onError: (error?: Error) => handleError('LinkedIn', error),
      }
    : undefined;

  return (
    <SignUpLayout hasTrialOffer={Boolean(trialOfferCode)}>
      {/* Google's button is at most 400px wide, so LinkedIn's and Slack's match it. */}
      <div className="mx-auto w-full max-w-[400px] space-y-3">
        {inAppBrowser !== 'none' ? (
          // Google blocks sign-in in apps' built-in browsers, so LinkedIn
          // leads and Google hands off to the real browser. Slack stays out:
          // its sign-in hasn't been checked inside those browsers.
          <>
            {linkedIn && <LinkedInLogin {...linkedIn} prominent />}
            <InAppGoogleSignUp
              app={inAppBrowser}
              os={detectMobileOs(navigator.userAgent, navigator.maxTouchPoints)}
              linkedIn={linkedIn}
            />
          </>
        ) : (
          <>
            <GoogleLogin
              onSuccess={() => handleSuccess('Google')}
              onError={(error) => handleError('Google', error)}
              trialOfferCode={trialOfferCode}
              acceptTerms={acceptTerms}
              intent="signUp"
              text="signup_with"
            />
            {isSlackSsoConfigured && (
              <SlackLogin
                returnTo={oauthReturnTo}
                acceptTerms={acceptTerms}
                intent="signUp"
                onError={(error) => handleError('Slack', error)}
              />
            )}
            {linkedIn && <LinkedInLogin {...linkedIn} />}
          </>
        )}
      </div>
      {error && <AuthError message={error} />}
      <p className="text-muted-foreground text-center text-[13px]">
        {offerNotice}
      </p>
      <ContinueOnComputer />
      <hr className="border-border" />
      <TermsNotice
        lead="By signing up"
        terms={terms}
        className="text-muted-foreground text-center text-xs leading-[1.6]"
      />
    </SignUpLayout>
  );
};
